import React, { useState, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { enterInstitute } from "../../store/slices/instituteContextSlice";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  ArrowBack,
  Download,
  FilterList,
  Business,
  People,
  School,
  Search,
  Login,
} from "@mui/icons-material";

const InstitutesAnalytics = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.role?.slug || user?.role || "student";

  // This page is only for Super Admin
  const isSuperAdmin = userRole === "superadmin";

  const [searchQuery, setSearchQuery] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return;
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const analyticsService = (await import("../../services/analyticsService")).default;
        const res = await analyticsService.getInstitutes();
        if (mounted) {
          const data = res?.data?.data ?? res?.data ?? [];
          setInstitutes(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.warn("Failed to load institutes analytics:", e?.message || e);
        if (mounted) setInstitutes([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => (mounted = false);
  }, [isSuperAdmin]);

  const handleEnterInstitute = (institute) => {
    dispatch(enterInstitute(institute));
    navigate("/dashboard");
  };

  const columns = [
    {
      field: "name",
      headerName: "Institute",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 40, height: 40 }}>
            <Business />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.instituteCode || params.row.code || ""}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { field: "instituteCode", headerName: "Code", width: 100 },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="primary"
          title="Manage Institute"
          onClick={() => handleEnterInstitute({ id: params.row.id, name: params.row.name })}
        >
          <Login />
        </IconButton>
      ),
    },
  ];

  const filteredInstitutes = institutes.filter((inst) => {
    const matchesSearch =
      (inst.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (inst.instituteCode || inst.code || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlan = !planFilter || inst.plan === planFilter;
    const matchesStatus = !statusFilter || (inst.status || "active") === statusFilter;
    return matchesSearch && matchesPlan && matchesStatus;
  });

  const stats = [
    {
      label: "Total Institutes",
      value: institutes.length,
      color: "primary.main",
      icon: <Business />,
    },
    {
      label: "Active",
      value: institutes.filter((i) => (i.status || "active") === "active").length,
      color: "success.main",
      icon: <Business />,
    },
  ];

  // Show access denied for non-superadmin
  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>
            Access Denied
          </Typography>
        </Box>
        <Alert severity="error">
          You do not have permission to view this page. Only Super Admins can
          access the Institutes Overview.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Institutes Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage all registered institutes
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Download />}>
          Export
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      sx={{ color: stat.color }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: stat.color, width: 48, height: 48 }}>
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <FilterList color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Filters
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search institutes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Plan</InputLabel>
                <Select
                  value={planFilter}
                  label="Plan"
                  onChange={(e) => setPlanFilter(e.target.value)}
                >
                  <MenuItem value="">All Plans</MenuItem>
                  <MenuItem value="Enterprise">Enterprise</MenuItem>
                  <MenuItem value="Professional">Professional</MenuItem>
                  <MenuItem value="Basic">Basic</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Card>
        <CardContent>
          <DataGrid
            rows={filteredInstitutes}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={{ border: "none" }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default InstitutesAnalytics;
